import { FormContext } from "common/components/Form/FormContext";
import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputNumber from "rc-input-number";
import { useValidate } from "common/components/Form/hooks/useValidate";
import { Nullable } from "common/types";
import { round } from "common/helpers/Math";
import { parsePath } from "common/helpers/FormPath";
import { RangeInput } from "common/components/RangeInput";

export interface FormControlDataProps {
  /** Text to display in the field label */
  label: string;
  /** Accessible name for the input; falls back to `label` when omitted. */
  ariaLabel?: string;
  /** HTML name of the form element. This should match the record's key name. */
  name: string;
  /** The input element id. If undefined, "name" will be used */
  id: string;
  /** The input element classes */
  className: string;
  /** Optional input type */
  type?: "inputText" | "inputNumber" | "textarea" | "range";
  /** Optional HTML input type */
  htmlType?: string;
  /** Short hint that describes the expected value of an input field */
  placeholder?: string;
  /** Optional input validator */
  validate?: (value: string, rootRecord?: any) => Nullable<string>;
  /** If the input field should be disabled */
  disabled?: boolean;
  /** Focus on the input upon load */
  autoFocus?: boolean;
  /** Optional number of decimal places to be rounded, defaults to 2 */
  precision?: number;
  /** Change value by this amount when using up/down */
  step?: number;
  /** Optional minimum possible value */
  minValue?: number;
  /** Optional maximum possible value */
  maxValue?: number;
  /** Optional callback function to be called on input */
  onChange?: (value: string) => void;
  /** Optional prefix to add to the value */
  prefix?: React.ReactNode;
  /** Optional value if we want to show a read only value */
  readOnlyValue?: number | string;
  /** When true, an empty text input dispatches `null` instead of `""`. */
  emptyAsNull?: boolean;
}

export const FormControlData: React.FC<FormControlDataProps> = ({
  label,
  ariaLabel,
  name,
  id,
  className,
  type,
  htmlType,
  placeholder,
  validate,
  disabled,
  autoFocus,
  precision,
  step,
  minValue,
  maxValue,
  onChange,
  prefix,
  readOnlyValue,
  emptyAsNull,
}) => {
  if (readOnlyValue !== undefined && !disabled) {
    console.warn(
      "readOnlyValue provided with an enabled form control data. Read only values are only intended to be used with disabled fields.",
    );
  }

  const dispatch = useDispatch();
  const { rootPath, updateFormReducer } = useContext(FormContext);
  const path = parsePath(name);
  let value = useSelector((state: any) => state.getIn(rootPath.concat(path)));
  value = readOnlyValue ?? value;
  const nameId = id || name;
  const inputAriaLabel = ariaLabel ?? label;

  useValidate(validate, name);

  const handleInput = (event: any) => {
    const { value, name } = event.target;
    const dispatchValue = emptyAsNull && value === "" ? null : value;
    dispatch(updateFormReducer({ rootPath, name, value: dispatchValue }));

    if (onChange) onChange(value);
  };

  const handleNumberInput = (value: any) => {
    if (value === "" || value === null) {
      handleInput({ target: { value: null, name } } as any);
    } else if (typeof value === "number") {
      value = round(value, precision);
      handleInput({ target: { value, name } } as any);
    }
  };

  return (
    <>
      {type === "inputNumber" ? (
        <InputNumber
          inputMode="numeric"
          name={name}
          id={nameId}
          onChange={handleNumberInput}
          placeholder={placeholder}
          className={className}
          value={value}
          aria-label={inputAriaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
          controls={false}
          min={minValue}
          max={maxValue}
          prefix={prefix}
          step={step}
        />
      ) : type === "textarea" ? (
        <textarea
          name={name}
          placeholder={placeholder}
          id={nameId}
          className={className}
          onChange={handleInput}
          value={value || ""}
          aria-label={inputAriaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      ) : type === "range" ? (
        <RangeInput
          name={name}
          id={nameId}
          min={minValue}
          max={maxValue}
          step={step}
          value={value}
          onChange={handleNumberInput}
          disabled={disabled}
          label={inputAriaLabel}
        />
      ) : (
        <input
          type={htmlType || "text"}
          name={name}
          placeholder={placeholder}
          id={nameId}
          className={className}
          onChange={handleInput}
          value={value || ""}
          aria-label={inputAriaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      )}
    </>
  );
};
